import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseApi, CourseDetails } from '../../hooks/useCourseApi';
import { checkEnrollment } from '../../services/enrollmentService';
import { API_BASE_URL } from '../../apiConfig';
import { handleCourseImageError, resolveCourseImageUrl } from '../../utils/courseImage';
import { getTermInArabic } from '../../utils/gradeTranslations';
import axios from 'axios';
import { getTenantHeaders } from '../../config/tenant';
import { 
  BookOpen, 
  Clock, 
  Users, 
  Star, 
  Play, 
  ArrowRight, 
  CheckCircle, 
  Award,
  GraduationCap,
  Calendar,
  Video,
  Target,
  Zap,
  MessageCircle,
  Phone,
  ExternalLink
} from 'lucide-react';

interface ContactInfo {
  id: number;
  whatsApp_Number: string;
  facebook_Page: string;
  youTube_Channel: string;
  tiktokChannel: string;
}

interface ContactApiResponse {
  success: boolean;
  message: string;
  data: ContactInfo[];
}

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { fetchCourseDetailsWithoutProgress, isLoading, error } = useCourseApi();

  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(true);
  
  const [whatsappNumber, setWhatsappNumber] = useState('');

  useEffect(() => {
    const loadCourseDetails = async () => {
      if (id) {
        const response = await fetchCourseDetailsWithoutProgress(id);
        if (response?.success) {
          setCourseDetails(response.data);
        }
      }
    };

    loadCourseDetails();
  }, [id, fetchCourseDetailsWithoutProgress]);

  useEffect(() => {
    const checkUserEnrollment = async () => {
      if (id && currentUser?.id) {
        setIsCheckingEnrollment(true);
        const enrolled = await checkEnrollment(currentUser.id, id);
        setIsEnrolled(enrolled);
        setIsCheckingEnrollment(false);
      } else {
        setIsCheckingEnrollment(false);
      }
    };

    checkUserEnrollment();
  }, [id, currentUser?.id]);

  useEffect(() => {
    const fetchWhatsAppNumber = async () => {
      try {
        const response = await axios.get<ContactApiResponse>(`${API_BASE_URL}/Contact/getAll`, {
          headers: {
            ...getTenantHeaders()
          }
        });
        if (response.data.success && response.data.data.length > 0) {
          setWhatsappNumber(response.data.data[0].whatsApp_Number);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp number:', error);
      }
    };

    fetchWhatsAppNumber();
  }, []);

  const handleContinue = () => {
    navigate(`/course/${id}/curriculum`);
  };

  const handleWhatsAppContact = () => {
    if (whatsappNumber) {
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=مرحباً، أريد الاستفسار عن التسجيل في المنهج: ${courseDetails?.courseName}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto absolute inset-0 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <p className="text-gray-600 text-lg font-medium">جاري تحميل تفاصيل المنهج...</p>
        </div>
      </div>
    );
  }

  if (error || !courseDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center">

      </div>
    );
  }
  
  const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);

  return (
    <div className="min-h-screen bg-white font-cairo" dir="rtl">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 text-gray-900 overflow-hidden pt-4 sm:pt-6">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-20 sm:opacity-30">
            <div className="absolute top-10 left-10 w-48 h-48 sm:w-72 sm:h-72 bg-blue-200 rounded-full blur-2xl sm:blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-64 h-64 sm:w-96 sm:h-96 bg-purple-200 rounded-full blur-2xl sm:blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-56 h-56 sm:w-80 sm:h-80 bg-indigo-200 rounded-full blur-2xl sm:blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            {/* Course Image */}
            <div className="order-1">
              <div className="relative mx-auto max-w-xs sm:max-w-sm">
                <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl sm:rounded-3xl blur-xl sm:blur-2xl"></div>
                <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border border-white/50 transform hover:scale-[1.02] transition-all duration-500">
                  <img 
                    src={resolveCourseImageUrl(courseDetails.imagePath)} 
                    alt={courseDetails.courseName}
                    className="w-full h-auto object-cover"
                    onError={handleCourseImageError}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-gray-900/20 to-transparent"></div>
                  
                  {/* Course Stats Overlay */}
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                    <div className="flex items-center gap-2 sm:gap-3 text-white">
                      <div className="flex items-center gap-1 sm:gap-2 bg-white/20 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5">
                        <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">{totalLessons} درس</span>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Course Info */}
            <div className="order-2 text-center lg:text-right">
              {/* Course Badge */}
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full mb-4 sm:mb-6 gap-2 sm:gap-3 border border-gray-200/50 shadow-sm">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full animate-pulse"></div>
                <span className="text-purple-700 text-xs sm:text-sm font-medium">{getTermInArabic(courseDetails.term)}</span>
              </div>

              {/* Course Title */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-gray-900">
                {courseDetails.courseName}
              </h1>

              {/* Course Description */}
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {courseDetails.description}
              </p>
              
              {/* Course Features */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 justify-center lg:justify-start">
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-gray-200/50 shadow-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">الفصل</div>
                      <div className="text-xs font-semibold text-gray-900">{getTermInArabic(courseDetails.term)}</div>
                    </div>
                  </div>
                </div>
                {!isEnrolled && (
                <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-gray-200/50 shadow-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-lg flex items-center justify-center">
                      <Award className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">السعر</div>
                      <div className="text-xs font-semibold text-gray-900">
                        {courseDetails.price === 0 ? 'مجاناً' : `${courseDetails.price.toFixed(2)} جنيه`}
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Enrollment Section */}
              <div className="space-y-3 sm:space-y-4">
                {isCheckingEnrollment ? (
                  <div className="flex items-center justify-center gap-2 sm:gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-gray-400 border-t-transparent"></div>
                    <span className="text-gray-600 text-xs sm:text-sm">جاري التحقق من التسجيل...</span>
                  </div>
                ) : isEnrolled ? (
                  <button
                    onClick={handleContinue}
                    className="w-full lg:w-auto px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                  >
                    <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    استمرار المنهج
                  </button>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {courseDetails.price === 0 ? (
                      <button
                        onClick={() => navigate(`/course/${id}/curriculum`)}
                        className="w-full lg:w-auto px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                      >
                        <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        ابدأ المنهج المجاني
                      </button>
                    ) : (
                      <div className="space-y-3 sm:space-y-4">
                        <button
                          onClick={handleWhatsAppContact}
                          className="w-full lg:w-auto px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium text-xs sm:text-sm hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                        >
                          <MessageCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          التواصل مع الإشراف
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-8 sm:py-12 lg:py-16 bg-white relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 border border-gray-100">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-6 sm:mb-8 lg:mb-10">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">نظرة عامة على المنهج</h2>
                <p className="text-sm sm:text-base text-gray-600">
                  تعرف على تفاصيل المنهج وأهدافها التعليمية
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900 flex items-center gap-2 sm:gap-3">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    وصف المنهج
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{courseDetails.description}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-sm border border-emerald-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900 flex items-center gap-2 sm:gap-3">
                      <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                      تفاصيل المنهج
                    </h3>
                    <ul className="space-y-2 sm:space-y-3">
                      <li className="flex items-center gap-2 sm:gap-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">الفصل الدراسي: {getTermInArabic(courseDetails.term)}</span>
                      </li>
                      <li className="flex items-center gap-2 sm:gap-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-pink-600" />
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">عدد الدروس: {totalLessons} درس</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-sm border border-purple-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900 flex items-center gap-2 sm:gap-3">
                      <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                      مميزات المنهج
                    </h3>
                    <ul className="space-y-2 sm:space-y-3">
                      <li className="flex items-center gap-2 sm:gap-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">شرح تفصيلي ومفصل</span>
                      </li>
                      <li className="flex items-center gap-2 sm:gap-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-indigo-600" />
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">تمارين وتطبيقات عملية</span>
                      </li>
                      <li className="flex items-center gap-2 sm:gap-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">دعم متواصل من المدرس</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail; 
